/**
 * ONE SEAT TICKET, HELD ACROSS ONE NAVIGATION.
 *
 * ── THE PROBLEM ─────────────────────────────────────────────────────────────
 * The lobby screen is the META chunk and the match is the MATCH chunk (ADR-
 * 0005): the modes hub may not import `engine/match/**`, so it cannot seat
 * anything itself. It mints a ticket and the router sends the player to the
 * arena, where a different chunk opens the socket.
 *
 * A ticket is returned by `POST /modes/lobbies/:code/ticket` EXACTLY ONCE — the
 * server stores only its SHA-256 — so it cannot be fetched again on the other
 * side of that navigation. Something has to carry it, and a module-level
 * variable does not survive a full page load (a shared link, a refresh, an
 * external return).
 *
 * ── WHY `sessionStorage`, AND WHY THAT IS SAFE ──────────────────────────────
 * Per tab, cleared when the tab closes, never sent to a server, and unreadable
 * by another origin. What it holds is a bearer credential for ONE SEAT of ONE
 * LOBBY, valid for ten minutes, checked against the socket's own authenticated
 * account — a copy of it in another account's hands is refused with the same
 * answer an unknown ticket gets. It is not a session, it cannot be spent twice,
 * and it buys nothing but the right to sit in a match somebody already invited
 * you to.
 *
 * It is nonetheless a credential, so:
 *   • `takePendingSeat()` is a POP. Reading it removes it, so a stale ticket
 *     cannot be replayed into an unrelated later match;
 *   • it is dropped as soon as it is used, and on `clearPendingSeat()`;
 *   • every access is wrapped — a browser with storage disabled (private mode,
 *     a locked-down enterprise profile) throws on the property access itself,
 *     and losing a lobby to that is worse than an ephemeral fallback.
 *
 * ── THE FALLBACK ────────────────────────────────────────────────────────────
 * When storage is unavailable the seat lives in a module variable, which covers
 * the ordinary in-app navigation this exists for and loses the ticket on a full
 * reload. The player mints another one from the same lobby; the old one is
 * revoked by the mint. Nothing breaks, and no state is silently wrong.
 */
import type { MatchSeat } from './lobby-api';

/** The `sessionStorage` key. Per tab, per origin. */
export const PENDING_SEAT_KEY = 'rga.pendingSeat';

/** The in-memory fallback for a browser that refuses storage. */
let inMemory: MatchSeat | null = null;

function storage(): Storage | null {
  try {
    // The property access ITSELF throws in some locked-down browsers, which is
    // why this is inside the try rather than a `in` check.
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Is this really a seat, and not a leftover from an older build? */
function isSeat(value: unknown): value is MatchSeat {
  if (value === null || typeof value !== 'object') return false;
  const seat = value as Record<string, unknown>;
  return (
    typeof seat.ticket === 'string' &&
    seat.ticket !== '' &&
    typeof seat.code === 'string' &&
    typeof seat.lobbyId === 'string' &&
    typeof seat.kind === 'string'
  );
}

/** Remember the seat the arena is about to spend. Replaces any earlier one. */
export function stashPendingSeat(seat: MatchSeat): void {
  inMemory = seat;
  const store = storage();
  if (store === null) return;
  try {
    store.setItem(PENDING_SEAT_KEY, JSON.stringify(seat));
  } catch {
    /* quota, private mode, a disabled store — the in-memory copy stands */
  }
}

/**
 * TAKE the pending seat, if there is one. A pop, not a read.
 *
 * The arena calls this once on entry: a ticket is for one match, and leaving it
 * behind would mean a later, unrelated visit to the arena trying to redeem a
 * dead lobby and being refused for reasons the player could not explain.
 */
export function takePendingSeat(): MatchSeat | null {
  const held = inMemory;
  inMemory = null;
  const store = storage();
  if (store === null) return held;
  let raw: string | null = null;
  try {
    raw = store.getItem(PENDING_SEAT_KEY);
    store.removeItem(PENDING_SEAT_KEY);
  } catch {
    return held;
  }
  if (raw === null) return held;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isSeat(parsed) ? parsed : held;
  } catch {
    return held;
  }
}

/** Look without taking — for a screen that wants to say "you have a seat". */
export function peekPendingSeat(): MatchSeat | null {
  const store = storage();
  if (store === null) return inMemory;
  try {
    const raw = store.getItem(PENDING_SEAT_KEY);
    if (raw === null) return inMemory;
    const parsed: unknown = JSON.parse(raw);
    return isSeat(parsed) ? parsed : inMemory;
  } catch {
    return inMemory;
  }
}

/** Drop it — the player went somewhere else, or the lobby died. */
export function clearPendingSeat(): void {
  inMemory = null;
  const store = storage();
  if (store === null) return;
  try {
    store.removeItem(PENDING_SEAT_KEY);
  } catch {
    /* nothing to do: there is no state we could be leaving wrong */
  }
}

/* ═══════════════════════════════════════════════ the training difficulty ══ */

/**
 * THE OTHER THING THE MODES HUB DECIDES AND THE ARENA HAS TO HONOUR.
 *
 * Training's panel lists the five scripted difficulties the SERVER published
 * and calls back with the one the player chose — and until this existed the
 * router answered that choice with a bare `navigate('/arena')`, which dropped
 * it. The arena then opened its own menu and asked the same question again, so
 * picking "Brutal" in the hub and landing on a screen defaulted to something
 * else was the actual shipped behaviour.
 *
 * It rides here rather than in the URL because a difficulty is not a location:
 * `/arena?difficulty=5` is bookmarkable, shareable and editable, and would make
 * a stale tab reopen a match the player never asked for. It is a NUMBER, not a
 * credential — losing it costs one tap on the arena's own menu, which is
 * exactly the fallback.
 */
export const PENDING_TRAINING_KEY = 'rga.pendingTraining';

let inMemoryTraining: number | null = null;

/** Remember the difficulty the hub chose. Replaces any earlier one. */
export function stashPendingTraining(difficulty: number): void {
  if (!Number.isInteger(difficulty)) return;
  inMemoryTraining = difficulty;
  const store = storage();
  if (store === null) return;
  try {
    store.setItem(PENDING_TRAINING_KEY, String(difficulty));
  } catch {
    /* the in-memory copy covers the ordinary in-app navigation */
  }
}

/**
 * Take it, once. A POP for the same reason the seat is: a difficulty left
 * behind would start an unasked-for training match on the NEXT visit to the
 * arena, including the visit a Ladder player makes.
 */
export function takePendingTraining(): number | null {
  const held = inMemoryTraining;
  inMemoryTraining = null;
  const store = storage();
  if (store === null) return held;
  let raw: string | null = null;
  try {
    raw = store.getItem(PENDING_TRAINING_KEY);
    store.removeItem(PENDING_TRAINING_KEY);
  } catch {
    return held;
  }
  if (raw === null) return held;
  const parsed = Number.parseInt(raw, 10);
  return Number.isInteger(parsed) ? parsed : held;
}

/** Look without taking. */
export function peekPendingTraining(): number | null {
  const store = storage();
  if (store === null) return inMemoryTraining;
  try {
    const raw = store.getItem(PENDING_TRAINING_KEY);
    if (raw === null) return inMemoryTraining;
    const parsed = Number.parseInt(raw, 10);
    return Number.isInteger(parsed) ? parsed : inMemoryTraining;
  } catch {
    return inMemoryTraining;
  }
}
